import * as api from "../../../api/index";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  CheckCircleOutline,
  KeyboardArrowLeft,
  Star,
} from "@material-ui/icons";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { ALERT } from "../../../constants/actionTypes";

import "./Project.css";

const Project = ({
  showDetails,
  setShowDetails,
  quote,
  ongoing,
  inactive,
  completed,
  preview,
  getting,
}) => {
  dayjs.extend(relativeTime);
  const history = useHistory();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [showMessage, setShowMessage] = useState(false);
  const [files, setFiles] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [translation, setTranslation] = useState({});
  const [fetching, setFetching] = useState(true);
  const [rating, setRating] = useState(0);
  const [review, setReview] = useState("");
  const [sending, setSending] = useState(false);
  const disable = rating === 0 || review.trim() === "" || sending;

  const openModal = (translation) => {
    setTranslation(translation);
    document.querySelector(".client__details").open = true;
  };

  const closeModal = () => {
    document.querySelector(".client__details").open = false;
  };

  const cummulateSize = (translation) => {
    return translation.files.reduce((acc, file) => acc + file.size, 0);
  };

  const handleRating = async () => {
    setSending(true);

    try {
      await api.updateQuote(quote.id, { reviewed: true });
      await api.updateUser(translation.translator.id, {
        $inc: { "rating.total_stars": rating, "rating.total_rating": 1 },
        $push: { reviews: { message: review, projectName: quote.projectName } },
      });
      history.push("/projects/index");
    } catch (error) {
      if (error.response)
        dispatch({
          type: ALERT,
          payload: {
            severity: "error",
            message: error.response.data.message,
            duration: 5000,
            display: true,
          },
        });
      else
        dispatch({
          type: ALERT,
          payload: {
            severity: "error",
            message: "An unexpected error occurred",
            duration: 5000,
            display: true,
          },
        });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const getFile = async () => {
      try {
        const { data } = await api.getFiles(quote.id);
        setFiles(data.files);
        if (completed || preview) {
          const { data } = await api.getApproved(quote.id);
          setTranslations(data.approved);
        }
      } catch (error) {
        if (error.response)
          dispatch({
            type: ALERT,
            payload: {
              severity: "error",
              message: error.response.data.message,
              duration: 5000,
              display: true,
            },
          });
        else
          dispatch({
            type: ALERT,
            payload: {
              severity: "error",
              message: "An unexpected error occurred",
              duration: 5000,
              display: true,
            },
          });
      } finally {
        setLoading(false);
        setFetching(false);
      }
    };
    if (showDetails && quote && !getting) getFile();

    return () => setFiles([]);
  }, [showDetails, quote, completed, getting, preview, dispatch]);

  return (
    <>
      <div className="project position-fixed t-0 r-0 overflow-auto h-100 z-3">
        {getting ? (
          <div className="w-100 h-100 d-flex align-center justify-center">
            <div className="spin-large"></div>
          </div>
        ) : (
          <>
            <h2
              className="project__head d-flex align-center font-subhead p-40 pointer text-uppercase"
              onClick={() => setShowDetails(false)}
            >
              <KeyboardArrowLeft style={{ fontSize: "45px" }} />
              {quote.projectName}
            </h2>
            <div className="project__scroll">
              <div className="project__top project__min__width d-flex justify-between p-40 f-100">
                <div className="project__left f-40">
                  <h3 className="mb-20">Project Details</h3>
                  <div className="d-flex py-20">
                    <span className="font-bold">Project Name</span>
                    <span className="text-capitalise">{quote.projectName}</span>
                  </div>
                  <div className="d-flex py-20">
                    <span className="font-bold">Project ID</span>
                    <span className="text-capitalise">{quote.quote_id}</span>
                  </div>
                  <div className="d-flex py-20">
                    <span className="font-bold">Translation</span>
                    <span>
                      {quote.languageFrom} - {quote.languageTo.join(", ")}
                    </span>
                  </div>
                  <div className="d-flex py-20">
                    <span className="font-bold">Subject</span>
                    <span>{quote.subject}</span>
                  </div>
                  {(quote.status !== "pending" ||
                    quote.status !== "inactive") &&
                    !completed && (
                      <div className="d-flex py-20">
                        <span className="font-bold">Delivery Date</span>
                        <span>
                          {dayjs(quote.deliveryDate).format(
                            "dddd, MMMM D YYYY"
                          )}
                        </span>
                      </div>
                    )}
                  {(completed || preview) && (
                    <div className="d-flex py-20">
                      <span className="font-bold">Created On</span>
                      <span>
                        {dayjs(quote.createdAt).format("dddd, MMMM D YYYY")}
                      </span>
                    </div>
                  )}
                </div>
                <div className="project__right f-70">
                  <div>
                    <h3 className="px-40 py-5">Project Documents</h3>
                    <div className="grid c-4 px-40 pt-40 pb-20">
                      <span className="font-bolder">File Name</span>
                      <span className="font-bolder">Document Type</span>
                      <span className="font-bolder">Pages</span>
                      <span className="font-bolder">Size</span>
                    </div>
                    {loading ? (
                      <div className="fading__ctn grid c-4">
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20 ellipsis"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                      </div>
                    ) : (
                      <>
                        {files.map((file, index) => (
                          <div
                            className="project__file__info font-big grid c-4 py-10 px-40 font-sm"
                            key={file.name.concat(index)}
                          >
                            <span>
                              {file.name.substr(0, file.name.lastIndexOf("."))}
                            </span>
                            <span className="text-uppercase">
                              {file.name.substr(
                                file.name.lastIndexOf(".") + 1,
                                file.name.length
                              )}
                            </span>
                            <span>
                              {quote.totalPages ? file.page : file.count}
                            </span>
                            <div className="d-flex align-center justify-between">
                              <span>
                                {file.size < 1000
                                  ? `${file.size} B`
                                  : file.size > 999999.9
                                  ? `${(file.size / 1000000).toFixed(1)} MB`
                                  : `${(file.size / 1000).toFixed(1)} KB`}
                              </span>
                              <a
                                download={`${file.name.substr(
                                  0,
                                  file.name.lastIndexOf(".")
                                )}(${quote.projectName})-NT`}
                                href={file.base64}
                              >
                                <button className="project__file__btn border-0 outline-0 rounded py-5 px-10 font-bold">
                                  Preveiw
                                </button>
                              </a>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                  {inactive && (
                    <div>
                      <div className="grid c-4 font-bolder mt-40 mb-20 px-40 py-10">
                        <span>Quantity</span>
                        <span>Unit Price</span>
                        <span>Langs</span>
                        <span>USD</span>
                      </div>
                      {loading ? (
                        <div className="fading__ctn grid c-4 ">
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                      ) : (
                        <div className="project__grey font-big grid c-4 py-10 px-40 font-sm">
                          <span>
                            {quote.status === "pending"
                              ? "NA"
                              : quote.totalPages
                              ? `${quote.totalPages}${
                                  quote.totalPages > 1 ? "s" : ""
                                }`
                              : `${quote.totalCount} words`}
                          </span>
                          <span>
                            {quote.status === "pending"
                              ? "NA"
                              : quote.price.toFixed(2)}
                          </span>
                          <span>{quote.languageTo.length}</span>
                          <span>
                            {quote.status === "pending"
                              ? "NA"
                              : (
                                  (quote.price *
                                    quote.languageTo.length *
                                    100 *
                                    (quote.totalPages
                                      ? quote.totalPages
                                      : quote.totalCount)) /
                                  100
                                ).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  {!inactive && (
                    <div>
                      <div className="grid c-3 font-bolder mt-40 mb-20 px-40 py-10">
                        <span>Quantity</span>
                        <span>Total Files</span>
                        <span>Category</span>
                      </div>
                      {loading ? (
                        <div className="fading__ctn grid c-3">
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                      ) : (
                        <div className="project__grey font-big grid c-3 py-10 px-40 font-sm">
                          <span>
                            {quote.totalPages
                              ? `${quote.totalPages}${
                                  quote.totalPages > 1 ? "s" : ""
                                }`
                              : `${quote.totalCount} words`}
                          </span>
                          <span>{files.length}</span>
                          <span>{quote.category}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div>
                    <div className="grid c-5 font-bold mt-40 mb-20 px-40 py-10">
                      <span>Apostilles</span>
                      <span>Notarization</span>
                      <span>Sworn</span>
                      <span>VAT</span>
                      <span>Total</span>
                    </div>
                    {loading ? (
                      <div className="fading__ctn grid c-5">
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                        <span
                          style={{ height: "53px" }}
                          className="fade p-20"
                        ></span>
                      </div>
                    ) : (
                      <div className="project__grey font-big grid c-5 py-10 px-40 font-sm">
                        <span>{quote.addson.apostilles.toFixed(2)}</span>
                        <span>{quote.addson.notarization.toFixed(2)}</span>
                        <span>{quote.addson.sworn.toFixed(2)}</span>
                        <span>7.5%</span>
                        {!completed && (
                          <span>
                            {quote.payment.amount > 0
                              ? `$${
                                  Math.ceil(
                                    (quote.payment.amount / 100) * 100
                                  ) / 100
                                }`
                              : "NA"}
                          </span>
                        )}
                        {completed && (
                          <span>
                            {quote.amount > 0
                              ? `$${
                                  Math.ceil((quote.amount / 100) * 100) / 100
                                }`
                              : "NA"}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="d-flex justify-end p-5">
                    {
                      <button
                        className="project__btn__note border-0 outline-0 py-5 px-20 ml-10"
                        onClick={() => setShowMessage(true)}
                      >
                        Note
                      </button>
                    }
                  </div>
                </div>
              </div>
              <div className="project__bottom project__min__width">
                {(completed || preview) && (
                  <div className="mb-40">
                    <h3 className="px-40 mx-40 py-5">Project Documents</h3>
                    {fetching ? (
                      <div className="my-20">
                        <div className="fading__ctn grid c-5 mx-40 mb-20">
                          <span
                            style={{ height: "50px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                        <div className="fading__ctn grid c-5 mx-40">
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                        <div className="fading__ctn grid c-4 py-20 mx-40">
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                        <div className="fading__ctn grid c-5 mx-40">
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "40px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                        <div className="fading__ctn grid c-4 py-20 mx-40">
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                          <span
                            style={{ height: "53px" }}
                            className="fade p-20"
                          ></span>
                        </div>
                      </div>
                    ) : (
                      <>
                        {translations.length < 1 ? (
                          <div className="font-bigger font-bold p-20 text-center">
                            No Translations Available
                          </div>
                        ) : (
                          <>
                            {translations.map((translation) => (
                              <div key={translation.language}>
                                <div className="text-uppercase font-bold font-subhead mx-40 px-40 pt-10">
                                  {translation.language}
                                </div>
                                <div className="project__uploads__summary__top grid c-5 px-40 py-10 mx-40 mt-20">
                                  <span className="font-bolder">File Name</span>
                                  <span className="font-bolder">Language</span>
                                  <span className="font-bolder">
                                    Document Type
                                  </span>
                                  <span className="font-bolder">Size</span>
                                </div>
                                {translation.files.map((file, index) => (
                                  <div
                                    className="project__uploads__row font-big grid align-center c-5 my-20 py-5 px-40 mx-40 font-sm"
                                    key={file.name.concat(index)}
                                  >
                                    <span className="text-capitalise">
                                      {file.name}
                                    </span>
                                    <span className="text-capitalise">
                                      {translation.language}
                                    </span>
                                    <span className="text-uppercase">
                                      {file.type}
                                    </span>
                                    <span>
                                      {file.size < 1000
                                        ? `${file.size} B`
                                        : file.size > 999999.9
                                        ? `${(file.size / 1000000).toFixed(
                                            1
                                          )} MB`
                                        : `${(file.size / 1000).toFixed(1)} KB`}
                                    </span>
                                    <a
                                      download={`${file.name}(${quote.projectName})-NT`}
                                      href={file.base64}
                                    >
                                      <button className="projects__preview__btn outline-0 border-0 rounded p-10 w-75 font-bold font-normal hidden">
                                        Download
                                      </button>
                                    </a>
                                  </div>
                                ))}
                                <div className="project__uploads__summary__top grid align-center c-4 px-40 py-10 mx-40 mt-20">
                                  <span className="font-bolder">
                                    Project Name
                                  </span>
                                  <span className="font-bolder">
                                    Total Files
                                  </span>
                                  <span className="font-bolder">
                                    Total size
                                  </span>
                                </div>
                                <div className="project__uploads__summary font-big grid align-center c-4 my-20 py-5 px-40 mx-40 font-sm">
                                  <span>{quote.projectName}</span>
                                  <span>{translation.files.length}</span>
                                  <span>
                                    {cummulateSize(translation) < 1000
                                      ? `${cummulateSize(translation)} B`
                                      : cummulateSize(translation) > 999999.9
                                      ? `${(
                                          cummulateSize(translation) / 1000000
                                        ).toFixed(1)} MB`
                                      : `${(
                                          cummulateSize(translation) / 1000
                                        ).toFixed(1)} KB`}
                                  </span>
                                  {!quote.reviewed && (
                                    <button
                                      className="outline-0 border-0 rounded p-10 w-75 font-bold font-normal"
                                      onClick={() => openModal(translation)}
                                    >
                                      Rate
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
                {ongoing && (
                  <div className="d-flex flex-column my-20 mx-40">
                    <h3
                      style={{ backgroundColor: "#aaa" }}
                      className="p-10 px-20"
                    >
                      Progress Bar
                    </h3>
                    <div className="project__progress d-flex p-20">
                      <div
                        style={{
                          backgroundColor:
                            quote.status === "active" ||
                            quote.status === "ongoing" ||
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "rgb(0, 172, 0)"
                              : "",
                          color:
                            quote.status === "active" ||
                            quote.status === "ongoing" ||
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "#fff"
                              : "",
                        }}
                        className="d-flex flex-column justify-center align-center box-shadow-lg"
                      >
                        <CheckCircleOutline
                          style={{
                            color:
                              quote.status === "active" ||
                              quote.status === "ongoing" ||
                              quote.status === "completed" ||
                              quote.status === "approved"
                                ? "#fff"
                                : "",
                          }}
                          className="icon-orange mb-10"
                          fontSize="large"
                        />
                        <span className="font-bold">Activated</span>
                      </div>
                      <div
                        style={{
                          backgroundColor:
                            quote.status === "ongoing" ||
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "rgb(0, 172, 0)"
                              : "",
                          color:
                            quote.status === "ongoing" ||
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "#fff"
                              : "",
                        }}
                        className="d-flex flex-column justify-center align-center box-shadow-lg"
                      >
                        <CheckCircleOutline
                          style={{
                            color:
                              quote.status === "ongoing" ||
                              quote.status === "completed" ||
                              quote.status === "approved"
                                ? "#fff"
                                : "",
                          }}
                          className="icon-orange mb-10"
                          fontSize="large"
                        />
                        <span className="font-bold">Ongoing</span>
                      </div>
                      <div
                        style={{
                          backgroundColor:
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "rgb(0, 172, 0)"
                              : "",
                          color:
                            quote.status === "completed" ||
                            quote.status === "approved"
                              ? "#fff"
                              : "",
                        }}
                        className="d-flex flex-column justify-center align-center box-shadow-lg"
                      >
                        <CheckCircleOutline
                          style={{
                            color:
                              quote.status === "completed" ||
                              quote.status === "approved"
                                ? "#fff"
                                : "",
                          }}
                          className="icon-orange mb-10"
                          fontSize="large"
                        />
                        <span className="font-bold">Review</span>
                      </div>
                      <div
                        style={{
                          backgroundColor:
                            quote.status === "approved" ? "rgb(0, 172, 0)" : "",
                          color: quote.status === "approved" ? "#fff" : "",
                        }}
                        className="d-flex flex-column justify-center align-center box-shadow-lg"
                      >
                        <CheckCircleOutline
                          style={{
                            color: quote.status === "approved" ? "#fff" : "",
                          }}
                          className="icon-orange mb-10"
                          fontSize="large"
                        />
                        <span className="font-bold">Approved</span>
                      </div>
                    </div>
                  </div>
                )}
                {inactive && (
                  <div className="d-flex justify-center my-20 mx-40">
                    <button
                      className="project__btn__accept border-0 outline-0 p-10 rounded w-45 font-normal font-bold"
                      onClick={() => history.push(`/payment/${quote.id}`)}
                    >
                      Activate
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div
        style={{ right: showMessage ? 0 : "" }}
        className="project__declined__msg position-fixed h-100 t-0 z-4 overflow-auto"
      >
        <h3
          className="position-sticky d-flex align-center p-20 pointer border-bottom-1 t-0"
          onClick={() => setShowMessage(false)}
        >
          <KeyboardArrowLeft style={{ fontSize: "36px" }} />
          Note
        </h3>
        <div className="d-flex flex-column px-20 py-10">
          <span className="font-bold font-big">Translator's Note</span>
          <span className="lh-lg">I need a very good tranlation</span>
        </div>
      </div>
      {!quote.reviewed && (
        <details
          style={{ backgroundColor: "red" }}
          className="client__details position-absolute z-3"
        >
          <summary className="list-none"></summary>
          <div className="d-flex align-center justify-center position-fixed t-0 r-0 w-100 h-100 z-5">
            <div className="d-flex flex-column align-center text-center p-20">
              <h3 className="font-subhead">Rate Translator</h3>
              <div>
                <p className="font-heavy font-big py-10">
                  Help us to render a better service
                </p>
                <div className="w-100 ">
                  <p className="font-bolder font-big">Rate our service</p>
                  <div className="p-10">
                    <Star
                      style={{
                        color: rating > 0 ? "rgb(0, 172, 0)" : "",
                        fontSize: "40px",
                      }}
                      className="icon-light-grey pointer"
                      onClick={() => setRating(1)}
                    />
                    <Star
                      style={{
                        color: rating > 1 ? "rgb(0, 172, 0)" : "",
                        fontSize: "40px",
                      }}
                      className="icon-light-grey pointer"
                      onClick={() => setRating(2)}
                    />
                    <Star
                      style={{
                        color: rating > 2 ? "rgb(0, 172, 0)" : "",
                        fontSize: "40px",
                      }}
                      className="icon-light-grey pointer"
                      onClick={() => setRating(3)}
                    />
                    <Star
                      style={{
                        color: rating > 3 ? "rgb(0, 172, 0)" : "",
                        fontSize: "40px",
                      }}
                      className="icon-light-grey pointer"
                      onClick={() => setRating(4)}
                    />
                    <Star
                      style={{
                        color: rating > 4 ? "rgb(0, 172, 0)" : "",
                        fontSize: "40px",
                      }}
                      className="icon-light-grey pointer"
                      onClick={() => setRating(5)}
                    />
                  </div>
                </div>
                <label>
                  <p className="font-bolder font-big">Send a reveiw</p>
                  <textarea
                    className="mt-10 mb-40 w-100 border-0 outline-0 p-10 border-1 box-shadow-lg"
                    placeholder="..."
                    value={review}
                    onChange={(e) => setReview(e.target.value)}
                  />
                </label>
              </div>
              <div className="d-flex justify-between">
                <button
                  className="project__btn__cancel border-0 outline-0 p-10 font-normal font-bold w-45 rounded"
                  onClick={closeModal}
                  disabled={sending}
                >
                  Cancel
                </button>
                <button
                  style={{ opacity: disable ? "0.6" : "" }}
                  className="project__btn__accept d-flex justify-center align-center border-0 outline-0 p-10 font-normal font-bold w-45 rounded"
                  onClick={handleRating}
                  disabled={disable}
                >
                  Rate
                  {sending && <div className="spin-white ml-10"></div>}
                </button>
              </div>
            </div>
          </div>
        </details>
      )}
    </>
  );
};

export default Project;
